import React, { useEffect, useState, useRef } from 'react';
import { useStore } from '../context/StoreContext';
import { formatCurrency } from '../services/dataService';
import { Table, Product, OrderItem, UserRole } from '../types';
import { getPendingRequest } from '../services/pendingRequests';
import { Button } from '../components/Button';
import { ChevronLeft, Send, X, LogOut, ShoppingCart, Search } from 'lucide-react';
import { api } from '../services/api';

// Novo Componente para a tela de Seleção de Mesas
const TableSelectionScreen = ({ onSelectTable, onLogout }: { onSelectTable: (id: number) => void; onLogout: () => void }) => {
    const { tables, currentCompany } = useStore();
    
    // Filtra a mesa "Balcão" (ID 0)
    const displayTables = tables.filter(t => t.id !== 0);

    return (
        <div className="h-screen bg-gray-100 flex flex-col overflow-hidden">
            <header className="bg-white p-4 shadow-sm z-10 flex justify-between items-center border-b">
                <div>
                    <h1 className="text-gray-800 text-lg font-bold">FlowTab</h1>
                    <p className="text-gray-500 text-xs font-medium">{currentCompany?.name}</p>
                </div>
                <button onClick={onLogout} className="flex items-center gap-2 text-sm font-bold text-red-500 bg-red-50 hover:bg-red-100 px-3 py-2 rounded-lg transition-colors">
                    <LogOut className="w-4 h-4" /> Sair
                </button>
            </header>
            <div className="flex-1 overflow-y-auto p-4">
                 <p className="text-gray-500 text-sm mb-4 font-semibold">Selecione uma mesa para começar.</p>
                <div className="grid grid-cols-3 gap-3 pb-20">
                    {displayTables.map(table => {
                        const isPaying = table.status === 'PAYING';
                        const isOccupied = table.status === 'OCCUPIED';
                        
                        let cardClass = 'bg-white border-gray-200 text-gray-700 hover:border-brand-500';
                        if (isOccupied) cardClass = 'bg-blue-100 border-blue-400 text-blue-900';
                        if (isPaying) cardClass = 'bg-red-100 border-red-400 text-red-800';

                        return (
                            <button
                                key={table.id}
                                onClick={() => onSelectTable(table.id)}
                                className={`aspect-square rounded-2xl border-2 text-center flex flex-col items-center justify-center p-2 transition-all active:scale-95 shadow-sm ${cardClass}`}
                            >
                                <span className="text-xl font-bold block mb-1">{table.label}</span>
                                {table.name && <span className="text-[10px] font-black max-w-full truncate px-1">{table.name}</span>}
                                <span className="text-xs uppercase font-bold tracking-wider">
                                    {isPaying ? 'Conta' : isOccupied ? 'Ocupada' : 'Livre'}
                                </span>
                                {(isOccupied || isPaying) && (
                                    <span className={`mt-1 text-sm font-semibold px-2 py-0.5 rounded-md ${isPaying ? 'bg-red-200 text-red-800' : 'bg-blue-200 text-blue-800'}`}>
                                        {formatCurrency(table.total)}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// Novo Componente para a tela de Pedidos (redesenhado)
const OrderScreen = ({ table, onBack }: { table: Table; onBack: () => void }) => {
    const { user, products, categories, addMultipleOrdersToTable } = useStore();
    const [categoryFilter, setCategoryFilter] = useState<string>('Todos');
    const [searchTerm, setSearchTerm] = useState('');
    const [cart, setCart] = useState<{product: Product, quantity: number, notes: string}[]>([]);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const sending = useRef(false);
    const submission = useRef(crypto.randomUUID());
    useEffect(() => {
        const confirmed = (event: Event) => {
            if ((event as CustomEvent).detail.packet.body.clientSubmissionId !== submission.current) return;
            setCart([]);
            submission.current = crypto.randomUUID();
        };
        window.addEventListener('ft-request-confirmed', confirmed);
        return () => window.removeEventListener('ft-request-confirmed', confirmed);
    }, []);
    const [isSending, setIsSending] = useState(false);

    const categoryList = ['Todos', ...categories];
    const editingBlocked = () => sending.current || Boolean(user?.companyId && getPendingRequest(user.companyId));

    const addToCart = (product: Product) => {
        if (editingBlocked()) return;
        setCart(prev => {
            const existing = prev.find(p => p.product.id === product.id);
            if (existing) {
                return prev.map(p => p.product.id === product.id ? {...p, quantity: p.quantity + 1} : p);
            }
            return [...prev, { product, quantity: 1, notes: '' }];
        });
    };
    
    const updateCartQuantity = (productId: string, change: number) => {
        if (editingBlocked()) return;
        setCart(prev => {
            return prev.map(item => 
                item.product.id === productId 
                ? { ...item, quantity: Math.max(0, item.quantity + change) } 
                : item
            ).filter(item => item.quantity > 0);
        });
    };

    const updateCartNotes = (productId: string, notes: string) => {
        if (editingBlocked()) return;
        setCart(prev => prev.map(item => item.product.id === productId ? { ...item, notes } : item));
    };

    const sendOrder = async () => {
        if (sending.current || cart.length === 0) return;
        if (table.status === 'PAYING') {
            alert('Esta mesa já pediu a conta. Não é possível lançar mais itens.');
            return;
        }
        sending.current = true;
        setIsSending(true);
        try {
            const saved = await addMultipleOrdersToTable(table.id, cart.map(item => ({ ...item, waiterName: user?.name })), submission.current);
            if (!saved) return;
            setCart([]);
            setIsCartOpen(false);
            onBack();
            alert('Pedido confirmado!');
        } finally {
            sending.current = false;
            setIsSending(false);
        }
    };

    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filteredProducts = products.filter(product => {
        if (product.internalUseOnly === true || product.active === false || product.allowSale === false) return false;

        const matchesCategory = categoryFilter === 'Todos' || product.category === categoryFilter;
        const matchesSearch = !normalizedSearch
            || product.name.toLowerCase().includes(normalizedSearch)
            || product.category.toLowerCase().includes(normalizedSearch)
            || product.description?.toLowerCase().includes(normalizedSearch);

        return matchesCategory && matchesSearch;
    });

    const cartTotalItems = cart.reduce((acc, item) => acc + item.quantity, 0);

    return (
        <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
            {/* Cabeçalho */}
            <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shadow-sm z-20">
                <div className="flex items-center gap-2">
                    <button onClick={onBack} className="p-2 -ml-2 text-gray-600"><ChevronLeft /></button>
                    <div>
                        <h2 className="font-bold text-gray-900 leading-tight">{table.name ? `${table.label} - ${table.name}` : table.label}</h2>
                        <p className="text-xs font-medium text-gray-500">Total Atual: {formatCurrency(table.total)}</p>
                    </div>
                </div>
            </header>

            {/* Conteúdo Principal */}
            <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">
                {/* Esquerda: Comanda Atual */}
                <div className="w-full max-h-[26vh] shrink-0 lg:max-h-none lg:w-2/5 border-b lg:border-r lg:border-b-0 border-gray-200 flex flex-col bg-white">
                    <h3 className="text-sm font-bold px-4 py-3 border-b text-gray-600">Comanda Atual</h3>
                    <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2">
                        {table.items.length === 0 ? (
                            <p className="text-center text-sm text-gray-400 p-8">Mesa vazia.</p>
                        ) : (
                            table.items.map((item: OrderItem) => (
                                <div key={item.id} className="flex items-center text-sm p-2 bg-gray-50 rounded-lg">
                                    <span className="font-bold text-gray-600 mr-2">{item.quantity}x</span>
                                    <span className="flex-1 text-gray-800 truncate">{item.productName}</span>
                                    <span className="font-semibold text-gray-600 w-20 text-right">{formatCurrency(item.price * item.quantity)}</span>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Direita: Catálogo de Produtos */}
                <div className="flex-1 min-h-0 flex flex-col">
                    <div className="shrink-0 bg-white border-b border-gray-100 p-2 space-y-2">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                placeholder="Pesquisar produto..."
                                className="w-full h-11 pl-9 pr-3 rounded-2xl border border-gray-200 bg-gray-50 text-sm font-semibold outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
                            />
                        </div>

                        <select
                            value={categoryFilter}
                            onChange={(event) => setCategoryFilter(event.target.value)}
                            className="sm:hidden w-full h-10 rounded-2xl border border-gray-200 bg-white px-3 text-sm font-bold text-gray-700 outline-none focus:border-brand-500"
                        >
                            {categoryList.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>

                        <div className="hidden sm:flex overflow-x-auto whitespace-nowrap gap-2 pb-1">
                            {categoryList.map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => setCategoryFilter(cat)}
                                    className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${categoryFilter === cat ? 'bg-brand-600 text-white shadow-md' : 'bg-gray-100 text-gray-600'}`}
                                >{cat}</button>
                            ))}
                        </div>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto p-2 pb-28">
                        {filteredProducts.length === 0 ? (
                            <div className="h-full flex items-center justify-center text-center px-6">
                                <div>
                                    <p className="font-bold text-gray-700">Nenhum produto encontrado</p>
                                    <p className="text-sm text-gray-400 mt-1">Tente buscar por outro nome ou trocar a categoria.</p>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 content-start">
                                {filteredProducts.map(product => {
                                    const inCart = cart.find(i => i.product.id === product.id);
                                    return (
                                        <div key={product.id} className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col text-center overflow-hidden">
                                            <div className="aspect-square w-full overflow-hidden bg-gray-100">
                                                <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                                            </div>
                                            <div className="p-2 flex-1 flex flex-col justify-between">
                                                <div>
                                                    <h3 className="font-bold text-xs text-gray-800 leading-tight line-clamp-2 min-h-8">{product.name}</h3>
                                                    <p className="text-brand-600 font-bold text-xs mt-1">{formatCurrency(product.price)}</p>
                                                </div>
                                                {inCart ? (
                                                    <div className="flex items-center justify-center gap-1 bg-gray-100 rounded-lg p-1 mt-2 mx-auto">
                                                        <button onClick={() => updateCartQuantity(product.id, -1)} className="w-7 h-7 flex items-center justify-center bg-white rounded-md shadow text-red-500 font-bold">-</button>
                                                        <span className="font-bold w-5 text-center text-xs">{inCart.quantity}</span>
                                                        <button onClick={() => addToCart(product)} className="w-7 h-7 flex items-center justify-center bg-brand-600 rounded-md shadow text-white font-bold">+</button>
                                                    </div>
                                                ) : (
                                                    <button onClick={() => addToCart(product)} className="mt-2 w-full h-8 bg-brand-50 text-brand-600 rounded-lg flex items-center justify-center hover:bg-brand-100 font-bold text-[10px]">
                                                        ADICIONAR
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* Botão Flutuante do Carrinho */}
            {cartTotalItems > 0 && (
                <div className="absolute bottom-6 right-6 z-30">
                    <Button onClick={() => setIsCartOpen(true)} size="xl" className="rounded-full w-20 h-20 shadow-xl animate-bounce">
                        <ShoppingCart />
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full border-2 border-white">{cartTotalItems}</span>
                    </Button>
                </div>
            )}
            
            {/* Modal do Carrinho */}
            {isCartOpen && (
                 <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end justify-center p-4">
                     <div className="bg-white rounded-t-3xl w-full max-w-lg shadow-2xl p-6 relative animate-in slide-in-from-bottom-12 duration-300">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-gray-900">Revisar Pedido</h2>
                            <button onClick={() => setIsCartOpen(false)} className="p-2 text-gray-400 hover:bg-gray-100 rounded-full"><X/></button>
                        </div>
                        <div className="space-y-2 max-h-60 overflow-y-auto no-scrollbar pr-2">
                            {cart.map(item => (
                                <div key={item.product.id} className="flex items-center gap-3">
                                    <img src={item.product.image} alt={item.product.name} className="w-12 h-12 rounded-lg object-cover" />
                                    <div className="flex-1">
                                        <p className="font-bold text-sm">{item.product.name}</p>
                                        <p className="text-xs text-gray-500">{formatCurrency(item.product.price)}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => updateCartQuantity(item.product.id, -1)} className="w-7 h-7 rounded bg-gray-200 text-gray-700">-</button>
                                        <span className="w-5 text-center font-bold">{item.quantity}</span>
                                        <button onClick={() => updateCartQuantity(item.product.id, 1)} className="w-7 h-7 rounded bg-gray-200 text-gray-700">+</button>
                                    </div>
                                    <input
                                        value={item.notes}
                                        onChange={(event) => updateCartNotes(item.product.id, event.target.value)}
                                        placeholder="Observação: sem cebola, ponto da carne..."
                                        className="col-span-full mt-2 w-full p-2 bg-gray-50 border border-gray-200 rounded-lg text-xs outline-none focus:border-brand-500"
                                    />
                                </div>
                            ))}
                        </div>
                        <Button onClick={sendOrder} disabled={isSending} size="lg" className="w-full mt-6 h-16 rounded-2xl">
                            <Send className="mr-2" /> {isSending ? 'Confirmando...' : 'Enviar Pedido'}
                        </Button>
                     </div>
                 </div>
            )}
        </div>
    );
};

// Componente Principal WaiterApp
export const WaiterApp = () => {
  const { logout, tables, user, currentCompany } = useStore();
  const [selectedTableId, setSelectedTableId] = useState<number | null>(null);

  const selectedTable = tables.find(t => t.id === selectedTableId);

  useEffect(() => {
    if (user?.role !== UserRole.WAITER || !user.companyId || !currentCompany?.useShiftManagement) return;

    let isMounted = true;
    const checkCashierStatus = async () => {
      const shift = await api.cashierShifts.getStatus(user.companyId!);
      if (!isMounted) return;
      if (!shift || shift.status !== 'OPEN') {
        alert('Expediente encerrado. Entre novamente no próximo caixa.');
        logout();
      }
    };

    checkCashierStatus();
    const intervalId = window.setInterval(checkCashierStatus, 10000);
    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [currentCompany?.useShiftManagement, logout, user]);

  if (!selectedTable) {
      return <TableSelectionScreen onSelectTable={setSelectedTableId} onLogout={logout} />;
  }

  return <OrderScreen table={selectedTable} onBack={() => setSelectedTableId(null)} />;
};
